import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { TaskWorkspaceStore } from '../../../../../core/h3ml/state/task-workspace-store';
import { dashboardIcon } from '../../../../../core/shared/icons/side-panel/dashboard-icon';
import { imageIcon } from '../../../../../core/shared/icons/side-panel/image-icon';
import { regionOfInterestIcon } from '../../../../../core/shared/icons/side-panel/region-of-interest-icon';
import { pointCloudProjectionIcon } from '../../../../../core/shared/icons/toolbars/point-cloud-projection-icon';
import { SvgIcon } from '../../../../../cross-team-shared/icons/create-icon-definition';
import {
  isPointCloudOutput,
  isVideoOutput,
} from '../../../../../cross-team-shared/misc-utils/output-utils';
import { getTotalFusedOutputs } from '../../../../../cross-team-shared/misc-utils/project-utils';
import { SettingsPanelTabName } from '../../../../../team-apis/core/side-panel';

interface SettingsPanelButton {
  tabName: SettingsPanelTabName;
  icon: SvgIcon;
  tooltip: string;
}

@Component({
  selector: 'h3-settings-panel',
  templateUrl: './settings-panel.component.html',
  styleUrl: './settings-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SettingsPanelComponent {
  private readonly taskWorkspaceStore = inject(TaskWorkspaceStore);
  private readonly sidePanelStore = this.taskWorkspaceStore('sidePanel');

  tabName2d = '2d' as const;
  tabName3d = '3d' as const;
  tabNameLayout = 'layout' as const;
  tabNameROI = 'ROI' as const;

  buttons = this.buildButtons();
  selectedTabName$ = this.sidePanelStore('selectedSettingsTabName').$.pipe(
    map((tab) => tab ?? this.buttons[0].tabName),
  );

  setSelectedTab(tabName: SettingsPanelTabName) {
    this.sidePanelStore('selectedSettingsTabName').set(tabName);
  }

  private buildButtons() {
    const { project } = this.taskWorkspaceStore.state();
    const buttons: SettingsPanelButton[] = [];

    if (project.outputs.some(isPointCloudOutput)) {
      buttons.push(
        this.getButton(this.tabName3d, pointCloudProjectionIcon, '3D settings'),
      );
    }

    if (project.outputs.some(isVideoOutput)) {
      buttons.push(this.getButton(this.tabName2d, imageIcon, '2D settings'));
    }

    if (getTotalFusedOutputs(project) > 1) {
      buttons.push(this.getButton(this.tabNameLayout, dashboardIcon, 'Layout'));
    }

    if (this.shouldIncludeROITab()) {
      buttons.push(
        this.getButton(
          this.tabNameROI,
          regionOfInterestIcon,
          'Region of interest',
        ),
      );
    }

    return buttons;
  }

  private getButton(
    tabName: SettingsPanelTabName,
    icon: SvgIcon,
    tooltip: string,
  ) {
    return {
      tabName,
      icon,
      tooltip,
    };
  }

  private shouldIncludeROITab() {
    const { project } = this.taskWorkspaceStore.state();
    const hasPointCloud = project.outputs.some(isPointCloudOutput);
    const hasVideo = project.outputs.some(
      (output) =>
        isVideoOutput(output) && !output.meta.is_orthographic_projection,
    );
    return hasPointCloud && hasVideo;
  }
}
